<template>
  <div
    v-if="ready"
    class="canvas"
    :class="getSnippetClasses"
    :style="{
      height: template.canvas.height + 'px',
      width: template.canvas.width + 'px',
    }"
  >
    <element-renderer
      v-for="(elementId) in rootElementsReversed"
      v-show="!getElementById(elementId)?.layerHiddenInGraphics"
      :id="elementId"
      :key="elementId"
      :type="getElementById(elementId).type"
      :toggle="true"
    />
  </div>
</template>

<script>
import { defineAsyncComponent } from 'vue';
import { mapGetters } from 'vuex';

import Packs from '../services/packs';

export default {
  components: {
    ElementRenderer: defineAsyncComponent(() => import('./Elements/ElementRenderer')),
  },

  provide() {
    return { isRenderMode: true };
  },

  data() {
    return {
      ready: false,
      userFonts: [],
    };
  },

  computed: {
    ...mapGetters('graphicsTemplate', [
      'getTemplate',
      'getElementGroupMap',
      'getElementById',
    ]),

    template() {
      return this.getTemplate;
    },

    rootElementsReversed() {
      return [...this.getElementGroupMap.root].reverse();
    },

    snippets() {
      return this.$store.state.user.snippets;
    },

    customFonts() {
      return this.$store.state.user.customFonts;
    },

    getSnippetClasses() {
      let classname = '';

      if (this.template.snippets) {
        this.template.snippets.forEach((s) => {
          classname += ` snp-${s}`;
        });
      }

      return classname;
    },
  },

  watch: {
    customFonts(newVal) {
      if (newVal) {
        this.attachCustomFont();
      }
    },
  },

  /**
   * Check for query params to determine what to render.
   * `template` (query) - Templates from Packs & Templates API.
   * `templateId` (route param) - Used to render saved graphics for thumbnail gen.
   */
  async mounted() {
    if (!this.$route.query.template && !this.$route.params.templateId) {
      return;
    }

    if (+this.$route.query.template) {
      // Template rendering from Packs & Templates API
      const templateId = +this.$route.query.template;
      const template = await Packs.getTemplate(templateId);

      await this.$store.dispatch('graphicsTemplate/setAllGraphicsTemplateState', {
        fullTemplate: template.settings,
        options: { blacklist: true },
      });
    } else {
      // Otherwise load the user's template to render.
      const template = await this.$store.dispatch('getTemplate', this.$route.params.templateId);

      await this.$store.dispatch('graphicsTemplate/setAllGraphicsTemplateState', {
        fullTemplate: template,
        options: { blacklist: true }, // SHOULD THIS ALWAYS BE BAKED INTO THE ACTION?
      });

      this.$store.dispatch('setOwner', template?.owner);
    }

    this.$store.setInitialState();

    this.$nextTick(async () => {
      if (this.$store.state.user.owner) {
        await Promise.all([
          this.$store.dispatch('getUserSnippets').then(() => {
            try {
              this.registerCustomCSS();
            } catch (err) { /* noop */ }
          }),

          this.$store.dispatch('getFonts').then(() => {
            this.attachCustomFont();
          }),
        ]);
      }

      this.ready = true;
    });
  },

  methods: {
    registerCustomCSS() {
      const cssMarker = document.querySelector(
        "meta[name='custom-css-marker']",
      );

      if (this.template.snippets) {
        // Iterate over each snippet on this theme, and make sure the css gets added
        this.template.snippets.forEach((snpId) => {
          const snippetBlock = document.querySelector(`style#snp-${snpId}`);

          // We haven't yet added a style block for this snippet
          if (!snippetBlock) {
            // Find the snippet that matches this id and add the css block
            this.snippets.some((snippet) => {
              if (snippet._id !== snpId) {
                return false; // This isn't the right snippet, try the next one
              }

              const styleEl = document.createElement('style');
              styleEl.id = `snp-${snpId}`;
              styleEl.innerHTML = snippet.css;
              cssMarker.parentNode.insertBefore(styleEl, cssMarker);

              return true; // We found the snippet, we can stop iterating
            });
          }
        });
      }
    },

    async attachCustomFont() {
      // loop through the array to extract only the media_urls
      this.customFonts.forEach((element) => {
        this.userFonts.push({
          font: element.title.split('.')[0],
          url: element.alt ? element.alt.media_url : element.media_url,
          fontId: `f-${element._id}`,
        });
      });
      await this.createFontLinks();
    },
    createFontLinks() {
      let css = '';

      this.userFonts.forEach((font) => {
        const fontFace = `
          @font-face {
            font-family: '${font.fontId}';
            src: url('${font.url}');
            font-weight: bold;
            font-style: normal;
            font-display: block;
          }

        `;

        css += fontFace;
      });

      const styleEl = document.createElement('style');
      styleEl.id = 'customFonts';
      styleEl.innerHTML = css;

      document.head.appendChild(styleEl);
    },
  },
};
</script>
